<template>
  <form class="run-search-field" @submit.prevent="search">
    <span class="run-search-field__label">Run #</span>
    <InputText v-model="runNumber" class="run-search-field__input" type="text" aria-label="Run number" />
    <Button class="run-search-field__submit" icon="pi pi-search" severity="secondary" title="Find run" type="submit" />
  </form>
</template>

<script setup>
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';

// Jump to a run by number.
//
// It used to look the run up itself against a module of mock data and announce
// the result by dispatching a `clerk:findRun` event on `window` — a global
// channel that the run-list panel listened on, which made two components
// communicate through the document instead of through their parent. It reports
// the number now and the application decides what that run is and where it is.
//
// A `<form>` rather than a keydown handler, so Enter submits the way it does in
// every other field and the button is a real submit.
defineOptions({ name: 'RunSearchField' });

// What is typed in the box. `v-model` so the application can clear or seed it.
const runNumber = defineModel({ type: String, default: '' });

const emit = defineEmits(['search']);

function search() {
  const trimmed = runNumber.value.trim();
  if (trimmed) emit('search', trimmed);
}
</script>

<style scoped>
.run-search-field {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: var(--p-run-search-field-gap);
  padding: var(--p-run-search-field-padding);
  border: var(--p-run-search-field-border-width) solid var(--p-run-search-field-border-color);
  border-radius: var(--p-run-search-field-border-radius);
  background: var(--p-run-search-field-background);
}

.run-search-field__label {
  text-transform: uppercase;
  font-size: var(--p-run-search-field-label-font-size);
  font-weight: var(--p-run-search-field-label-font-weight);
  letter-spacing: var(--p-run-search-field-label-letter-spacing);
  color: var(--p-run-search-field-label-color);
}

/* InputText draws its own box; strip it back so the wrapper above carries the
   single visible field and the two do not nest. */
.p-inputtext.run-search-field__input,
.p-inputtext.run-search-field__input:enabled:focus {
  width: 56px;
  border: 0;
  outline: 0;
  box-shadow: none;
  background: transparent;
  font-family: var(--p-run-search-field-input-font-family);
  font-weight: var(--p-run-search-field-input-font-weight);
  font-size: var(--p-run-search-field-input-font-size);
  color: var(--p-run-search-field-input-color);
  padding: var(--p-run-search-field-input-padding);
}

.p-button.run-search-field__submit {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border: 0;
  border-radius: var(--p-run-search-field-submit-border-radius);
  background: var(--p-run-search-field-submit-background);
  color: var(--p-run-search-field-submit-color);
}
</style>
